<script>
  import { onMount, onDestroy } from 'svelte';
  import TagPicker from './TagPicker.svelte';
  import selectedTags from './store/selected-tags';
  import apiRequest from './api';
  import TagManager from './TagManager.svelte';

  let url;
  let title;
  let description;

  let submitted = false;
  let valid = true;
  let errors = {};

  let showTagManager = false;

  let postSize;
  let sizeWarning;

  let hostname;
  let isTwitter;
  let isBluesky;
  let isTumblr;
  let isCohost;

  $: postSize = ((url && encodeURIComponent(url.trim()).length) || 0)
    + ((title && encodeURIComponent(title.trim()).length) || 0)
    + ((description && encodeURIComponent(description.trim()).length) || 0)
    + $selectedTags.map((t) => t.tag).join(' ').length;

  $: sizeWarning = postSize >= 3900;

  $: if (url) {
    try {
      const u = new URL(url);
      hostname = u.hostname;
    } catch (e) {
      // ignore
    }
  }

  $: if (hostname) {
    isTwitter = /(^|\.)(twitter|x)\.com$/.test(hostname);
    isBluesky = hostname === 'bsky.app';
    isTumblr = /(^|\.)tumblr\.com$/.test(hostname);
    isCohost = hostname === 'cohost.org';
  }

  if (window.location.search) {
    const params = new URLSearchParams(window.location.search);
    if (params.has('url')) {
      url = params.get('url').trim();
    }
    const lastPin = getLastPin(url);
    if (lastPin) {
      title = lastPin.description;
      description = lastPin.extended;
      $selectedTags = lastPin.tags.split(/\s+/).map((tag) => ({ tag }));
    } else {
      if (params.has('title')) {
        title = params.get('title').trim();
      }
      if (params.has('desc')) {
        description = params.get('desc').trim();
      }
      if (url) {
        getPin(url).then((pin) => {
          if (pin) {
            title = pin.title;
            description = pin.description;
            $selectedTags = pin.tags.split(/\s+/).map((tag) => ({ tag }));
          } else {
            if (isTwitter) {
              $selectedTags = [{ tag: 'tweets' }, ...$selectedTags];
            }
            if (isBluesky) {
              $selectedTags = [{ tag: 'skeets' }, ...$selectedTags];
            }
            if (isTumblr) {
              $selectedTags = [{ tag: 'tumblr' }, ...$selectedTags];
            }
            if (isCohost) {
              $selectedTags = [{ tag: 'chosts' }, ...$selectedTags];
            }
          }
        });
      }
    }
  }

  async function onClickAdd() {
    submitted = true;
    valid = true;
    errors = {};

    if (!(url && url.trim())) {
      errors.url = 'missing URL';
      valid = false;
    }
    if (!(title && title.trim())) {
      errors.title = 'missing title';
      valid = false;
    }
    if (!valid) { return; }

    const pin = {
      url: url.trim(),
      description: title.trim(),
    };

    if (description && description.trim()) {
      pin.extended = description.trim();
    }

    if ($selectedTags.length > 0) {
      pin.tags = $selectedTags.map((p) => p.tag).join(' ');
    }

    const response = await addPin(pin);
    if (response.ok) {
      window.close();
    } else {
      valid = false;
      try {
        const body = await response.json();
        errors.response = body.response;
      } catch (e) {
        errors.response = await response.text();
      }
    }
  }

  async function addPin(pin) {
    localStorage.setItem('lastPin', JSON.stringify(pin));
    async function needsRetry(response) {
      if (response.status === 414) { return false; }
      const body = await response.json();
      return body.response !== 'done';
    }
    return apiRequest('/skewer/add', { post: pin, needsRetry });
  }

  async function getPin(pinUrl) {
    const response = await apiRequest(`/skewer/pin?url=${encodeURIComponent(pinUrl)}`);
    const body = await response.json();
    if (body && body.pin) {
      return body.pin;
    }
    return null;
  }

  function getLastPin(queryUrl) {
    const json = localStorage.getItem('lastPin');
    if (!json) { return null; }
    let lastPin;
    try {
      lastPin = JSON.parse(json);
    } catch (e) {
      return null;
    }
    if (lastPin.url === queryUrl) {
      return lastPin;
    }
    return null;
  }

  function onClickClear() {
    submitted = false;
    valid = true;
    errors = {};
    url = undefined;
    title = undefined;
    description = undefined;
    $selectedTags = [];
  }

  function onClickTags() {
    showTagManager = true;
  }

  function onTagManagerClose() {
    showTagManager = false;
  }

  function onMessage(evt) {
    if (typeof evt.data === 'string') {
      description = description.replace(/[\s\n]*$/, '');
      if (/[\u2013\u2014-]$/.test(description)) {
        // ends with endash or emdash or dash
        description += '\n';
      } else {
        description += '\n\n';
      }
      description += evt.data;
    }
  }

  onMount(() => {
    window.addEventListener('message', onMessage);
  });

  onDestroy(() => {
    window.removeEventListener('message', onMessage);
  });
</script>

{#if showTagManager}
  <TagManager on:close={onTagManagerClose}/>
{/if}

<div class:hide={!submitted || valid}>
  <ul>
    {#if errors.url}
      <li>{errors.url}</li>
    {/if}
    {#if errors.title}
      <li>{errors.title}</li>
    {/if}
    {#if errors.desc}
      <li>{errors.desc}</li>
    {/if}
    {#if errors.response}
      <li>{errors.response}</li>
    {/if}
  </ul>
</div>
<div class="size-warning" class:hide={!sizeWarning}>approx. pin size: {postSize}</div>
<div><input
    type="text"
    placeholder="url"
    autocomplete="off"
    autocapitalize="off"
    bind:value={url}
    class:error={Boolean(errors.url)}></div>
<div><input
    type="text"
    placeholder="title"
    autocomplete="off"
    bind:value={title}
    class:error={Boolean(errors.title)}></div>
<div><textarea
    placeholder="description"
    bind:value={description}
    class:error={Boolean(errors.description)}></textarea></div>
<TagPicker />
<div class="buttons">
  <button on:click={onClickAdd}>add</button>
  <button on:click={onClickClear}>clear</button>
  <div class="tags-wrapper">
    <button class="tags" on:click={onClickTags}>tags</button>
  </div>
</div>

<style>
  .error {
    border: 2px dashed red;
  }

  .hide {
    display: none;
  }

  .size-warning {
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    color: orange;
    font-weight: bold;
  }

  .buttons {
    display: flex;
    flex: 1;
  }

  .buttons button {
    margin-right: 0.25rem;
  }

  .buttons .tags-wrapper {
    flex: 2;
    display: flex;
    flex-direction: row-reverse;
  }
</style>
